import React, { useEffect, useState } from "react";
import { useModulePermission } from "../utils/useModulePermission";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Grid,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Divider,
  Card,
  CardContent,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Tooltip,
  Stack,
  TableContainer,
  FormHelperText,
  Snackbar,
  Pagination,
  TablePagination,
  Autocomplete,
  InputAdornment,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  FileDownload as FileDownloadIcon,
  CalendarToday as CalendarIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AccessTime as AccessTimeIcon,
  EventRepeat as EventRepeatIcon,
} from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import {
  fetchPOClientNames,
  fetchPOProjectNames,
  fetchInvoices,
  fetchInvoicesFullList,
  updateInvoice,
  deleteInvoice,
  Invoice,
  InvoiceUpdateInput,
  InvoiceFilterParams,
} from "../api/po";

interface POAnalysisPageProps {
  startDate?: string | null;
  endDate?: string | null;
}

const InvoicesPage: React.FC<POAnalysisPageProps> = () => {
  const [invoiceFilterProjectNames, setInvoiceFilterProjectNames] = useState<
    string[]
  >([]);
  const [loadingInvoiceFilterProjects, setLoadingInvoiceFilterProjects] =
    useState<boolean>(false);
  const [invoiceFilterProjectNamesError, setInvoiceFilterProjectNamesError] =
    useState<string | null>(null);
  const [invoiceFilterClientNames, setInvoiceFilterClientNames] = useState<
    string[]
  >([]);
  const [loadingInvoiceFilterClients, setLoadingInvoiceFilterClients] =
    useState<boolean>(false);
  const [invoiceFilterClientNamesError, setInvoiceFilterClientNamesError] =
    useState<string | null>(null);

  const [invoicePage, setInvoicePage] = useState(1);
  const [invoicePageSize, setInvoicePageSize] = useState(20);
  const [invoiceTotal, setInvoiceTotal] = useState(0);
  const [invoiceTotalPages, setInvoiceTotalPages] = useState(0);

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState<boolean>(false);
  const [invoiceError, setInvoiceError] = useState<string | null>(null);
  const [invoiceFilters, setInvoiceFilters] = useState<InvoiceFilterParams>({
    status: "",
    client_name: "",
    project_name: "",
    from_date: "",
    to_date: "",
  });

  const [openEditInvoice, setOpenEditInvoice] = useState<boolean>(false);
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
  const [invoiceEditData, setInvoiceEditData] = useState<InvoiceUpdateInput>(
    {},
  );
  const [savingInvoice, setSavingInvoice] = useState<boolean>(false);
  const [invoiceEditError, setInvoiceEditError] = useState<string | null>(null);
  const [invoicePlannedValueFieldError, setInvoicePlannedValueFieldError] =
    useState<string | null>(null);
  const [expandedInvoiceIds, setExpandedInvoiceIds] = useState<Set<number>>(
    new Set(),
  );

  const [deleteInvoiceDialogOpen, setDeleteInvoiceDialogOpen] =
    useState<boolean>(false);
  const [deletingInvoice, setDeletingInvoice] = useState<Invoice | null>(null);
  const [deletingInvoiceLoading, setDeletingInvoiceLoading] =
    useState<boolean>(false);
  const [exportingInvoicesCsv, setExportingInvoicesCsv] = useState<boolean>(false);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const { can } = useModulePermission("Purchase Orders");

  // Helper function to format FastAPI validation errors
  const formatApiError = (error: any): string => {
    if (!error) return "An unknown error occurred";
    if (typeof error === "string") return error;
    if (Array.isArray(error)) {
      return error
        .map((err: any) => {
          const field =
            err.loc && err.loc.length > 1
              ? err.loc[err.loc.length - 1]
              : "field";
          return `${field}: ${err.msg || "Invalid value"}`;
        })
        .join(", ");
    }
    if (error.detail) return formatApiError(error.detail);
    if (error.message) return error.message;
    try {
      return JSON.stringify(error);
    } catch {
      return "An error occurred";
    }
  };

  const csvEscape = (value: unknown): string => {
    if (value === null || value === undefined) return "";
    const s = String(value);
    if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const formatCsvDate = (dateString: string | null): string => {
    if (!dateString) return "";
    const d = dayjs(dateString);
    return d.isValid() ? d.format("D-MMM-YY") : "";
  };

  const formatCsvOperationalMonthDate = (dateString: string | null): string => {
    if (!dateString) return "";
    const d = dayjs(dateString);
    return d.isValid() ? d.format("D-MMM") : "";
  };

  const computeInvoiceDueDate = (invoice: Invoice): string => {
    if (!invoice.invoice_date) return "";
    const match = (invoice.payment_terms ?? "").match(/\d+/);
    const days = match ? parseInt(match[0], 10) : NaN;
    if (!Number.isFinite(days) || days < 0) return "";
    const due = dayjs(invoice.invoice_date).add(days, "day");
    return due.isValid() ? due.format("D-MMM-YY") : "";
  };

  useEffect(() => {
    const clientName = invoiceFilters.client_name?.trim()
      ? invoiceFilters.client_name
      : undefined;
    let cancelled = false;
    (async () => {
      setLoadingInvoiceFilterProjects(true);
      setInvoiceFilterProjectNamesError(null);
      try {
        const names = await fetchPOProjectNames(clientName);
        const sorted = Array.from(new Set(names)).sort((a, b) =>
          a.localeCompare(b),
        );
        if (cancelled) return;
        setInvoiceFilterProjectNames(sorted);
        setInvoiceFilters((prev) => ({
          ...prev,
          project_name:
            prev.project_name && !sorted.includes(prev.project_name)
              ? ""
              : prev.project_name,
        }));
      } catch (err: any) {
        if (cancelled) return;
        const errorMessage = formatApiError(
          err.response?.data?.detail || err.response?.data || err.message,
        );
        setInvoiceFilterProjectNamesError(
          errorMessage || "Failed to load project names",
        );
      } finally {
        if (!cancelled) setLoadingInvoiceFilterProjects(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [invoiceFilters.client_name]);

  useEffect(() => {
    const projectName = invoiceFilters.project_name?.trim()
      ? invoiceFilters.project_name
      : undefined;
    let cancelled = false;
    (async () => {
      setLoadingInvoiceFilterClients(true);
      setInvoiceFilterClientNamesError(null);
      try {
        const names = await fetchPOClientNames(projectName);
        const sorted = Array.from(new Set(names)).sort((a, b) =>
          a.localeCompare(b),
        );
        if (cancelled) return;
        setInvoiceFilterClientNames(sorted);
        setInvoiceFilters((prev) => ({
          ...prev,
          client_name:
            prev.client_name && !sorted.includes(prev.client_name)
              ? ""
              : prev.client_name,
        }));
      } catch (err: any) {
        if (cancelled) return;
        const errorMessage = formatApiError(
          err.response?.data?.detail || err.response?.data || err.message,
        );
        setInvoiceFilterClientNamesError(
          errorMessage || "Failed to load client names",
        );
      } finally {
        if (!cancelled) setLoadingInvoiceFilterClients(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [invoiceFilters.project_name]);

  const buildInvoiceQueryParams = (
    page: number,
    pageSize: number,
  ): InvoiceFilterParams => ({
    status:
      invoiceFilters.status && invoiceFilters.status !== "All"
        ? invoiceFilters.status
        : undefined,
    client_name: invoiceFilters.client_name || undefined,
    project_name: invoiceFilters.project_name || undefined,
    from_date: invoiceFilters.from_date || undefined,
    to_date: invoiceFilters.to_date || undefined,
    page,
    page_size: pageSize,
  });

  const loadInvoices = async () => {
    setLoadingInvoices(true);
    setInvoiceError(null);
    try {
      const params = buildInvoiceQueryParams(invoicePage, invoicePageSize);
      const data = await fetchInvoices(params);
      setInvoices(data.items);
      setInvoiceTotal(data.total);
      setInvoiceTotalPages(data.total_pages);
    } catch (err: any) {
      const errorMessage = formatApiError(
        err.response?.data?.detail || err.response?.data || err.message,
      );
      setInvoiceError(errorMessage || "Failed to load invoices");
    } finally {
      setLoadingInvoices(false);
    }
  };

  useEffect(() => {
    loadInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoicePage, invoicePageSize]);


  const handleApplyInvoiceFilters = () => {
    setInvoicePage(1);
    loadInvoices();
  };

  const fetchAllInvoicesForExport = async (): Promise<Invoice[]> =>
    fetchInvoicesFullList({
      status:
        invoiceFilters.status && invoiceFilters.status !== "All"
          ? invoiceFilters.status
          : undefined,
      client_name: invoiceFilters.client_name || undefined,
      project_name: invoiceFilters.project_name || undefined,
      from_date: invoiceFilters.from_date || undefined,
      to_date: invoiceFilters.to_date || undefined,
    });

  const handleExportInvoicesCsv = async () => {
    try {
      setExportingInvoicesCsv(true);
      const allInvoices = await fetchAllInvoicesForExport();
      if (allInvoices.length === 0) {
        setSnackbar({
          open: true,
          message: "No invoices found for export.",
          severity: "error",
        });
        return;
      }

      const headers = [
        "Invoice_ID",
        "PO_ID / SOW_ID",
        "Account",
        "Project",
        "Milestone / Description",
        "Currency",
        "Exchange Rate",
        "EST_Org_Date",
        "EST_Date",
        "EST_Value",
        "EST_Value_USD",
        "Status",
        "Invoice_Date",
        "Invoiced_Value",
        "Invoiced_Value_USD",
        "Payment Terms",
        "Invoice_Due_Date",
        "Received_Date",
        "Received_Value",
        "Operational_Month_Date",
        "Operational_Month_Value",
      ];

      const rows = allInvoices.map((invoice) => {
        const estValueUsd = calculateUSDValue(
          invoice.est_value_original,
          invoice.fx_rate_usd,
        );
        const invoicedValueUsd = calculateUSDValue(
          invoice.invoiced_value_original,
          invoice.fx_rate_usd,
        );
        return [
          invoice.invoice_code || "",
          `PO-${invoice.po_id}`,
          invoice.client_name || "",
          invoice.project_name || "",
          invoice.description || "",
          invoice.currency || "",
          invoice.fx_rate_usd ?? "",
          formatCsvDate(invoice.finance_planned_date),
          formatCsvDate(invoice.operational_planned_date || invoice.finance_planned_date),
          invoice.est_value_original ?? "",
          estValueUsd ?? "",
          invoice.status || "",
          formatCsvDate(invoice.invoice_date),
          invoice.invoiced_value_original ?? "",
          invoicedValueUsd ?? "",
          invoice.payment_terms || "",
          computeInvoiceDueDate(invoice),
          formatCsvDate(invoice.payment_received_date),
          invoice.payment_received_value ?? "",
          formatCsvOperationalMonthDate(invoice.service_month_date),
          invoice.operational_revenue_value ?? "",
        ];
      });

      const csvContent = [
        headers.map(csvEscape).join(","),
        ...rows.map((row) => row.map(csvEscape).join(",")),
      ].join("\r\n");

      const blob = new Blob([csvContent], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `invoices_export_${dayjs().format("YYYYMMDD_HHmmss")}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSnackbar({
        open: true,
        message: `Exported ${allInvoices.length} invoices to CSV.`,
        severity: "success",
      });
    } catch (err: any) {
      const errorMessage = formatApiError(
        err.response?.data?.detail || err.response?.data || err.message,
      );
      setSnackbar({
        open: true,
        message: errorMessage || "Failed to export invoices.",
        severity: "error",
      });
    } finally {
      setExportingInvoicesCsv(false);
    }
  };

  const handleOpenEditInvoice = (invoice: Invoice) => {
    setEditingInvoice(invoice);
    setInvoiceEditData({
      status: invoice.status || undefined,
      finance_planned_date: invoice.finance_planned_date || null,
      operational_planned_date: invoice.operational_planned_date || null,
      service_month_date: invoice.service_month_date || null,
      invoice_date: invoice.invoice_date || null,
      est_value_original: invoice.est_value_original ?? null,
      invoiced_value_original: invoice.invoiced_value_original ?? null,
      fx_rate_usd: invoice.fx_rate_usd ?? null,
      fx_rate_inr: invoice.fx_rate_inr ?? null,
      payment_terms: invoice.payment_terms || null,
      payment_received_date: invoice.payment_received_date || null,
      payment_received_value: invoice.payment_received_value ?? null,
      remarks: invoice.remarks || null,
    });
    setOpenEditInvoice(true);
    setInvoiceEditError(null);
    setInvoicePlannedValueFieldError(null);
  };

  const handleCloseEditInvoice = () => {
    setOpenEditInvoice(false);
    setEditingInvoice(null);
    setInvoiceEditData({});
    setInvoiceEditError(null);
    setInvoicePlannedValueFieldError(null);
  };

  const handleSaveInvoice = async () => {
    if (!editingInvoice) return;
    setInvoiceEditError(null);
    setInvoicePlannedValueFieldError(null);
    const plannedRaw = invoiceEditData.est_value_original;
    const plannedNum =
      plannedRaw !== undefined && plannedRaw !== null ? Number(plannedRaw) : NaN;
    if (!Number.isFinite(plannedNum)) {
      setInvoicePlannedValueFieldError("Planned value is required");
      return;
    }
    setSavingInvoice(true);
    try {
      await updateInvoice(editingInvoice.id, {
        ...invoiceEditData,
        est_value_original: plannedNum,
      });
      await loadInvoices();
      setSnackbar({
        open: true,
        message: "Invoice updated successfully!",
        severity: "success",
      });
      handleCloseEditInvoice();
    } catch (err: any) {
      const errorMessage = formatApiError(
        err.response?.data?.detail || err.response?.data || err.message,
      );
      setInvoiceEditError(errorMessage || "Failed to update invoice");
    } finally {
      setSavingInvoice(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleDeleteInvoice = (invoice: Invoice) => {
    setDeletingInvoice(invoice);
    setDeleteInvoiceDialogOpen(true);
  };

  const handleConfirmDeleteInvoice = async () => {
    if (!deletingInvoice) return;

    setDeletingInvoiceLoading(true);
    try {
      await deleteInvoice(deletingInvoice.id);
      setSnackbar({
        open: true,
        message: `Invoice ${deletingInvoice.invoice_code} deleted successfully!`,
        severity: "success",
      });

      await loadInvoices();

      setDeleteInvoiceDialogOpen(false);
      setDeletingInvoice(null);
    } catch (err: any) {
      const errorMessage = formatApiError(
        err.response?.data?.detail || err.response?.data || err.message,
      );
      setSnackbar({
        open: true,
        message: errorMessage || "Failed to delete invoice",
        severity: "error",
      });
    } finally {
      setDeletingInvoiceLoading(false);
    }
  };

  const handleCloseDeleteInvoiceDialog = () => {
    if (!deletingInvoiceLoading) {
      setDeleteInvoiceDialogOpen(false);
      setDeletingInvoice(null);
    }
  };

  const formatCurrency = (value: number | null, currency: string | null) => {
    if (value === null || value === undefined) return "-";
    const currencyUpper = currency ? currency.toUpperCase() : "";
    const currencySymbol =
      currencyUpper === "USD"
        ? "$"
        : currencyUpper === "EUR"
          ? "€"
          : currencyUpper === "INR"
            ? "₹"
            : "";
    return `${currencySymbol}${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  // Calculate USD value from original value and FX rate (Base to USD)
  const calculateUSDValue = (
    originalValue: number | null,
    fxRateUSD: number | null,
  ): number | null => {
    if (
      originalValue === null ||
      originalValue === undefined ||
      fxRateUSD === null ||
      fxRateUSD === undefined
    )
      return null;
    return originalValue * fxRateUSD;
  };

  // Calculate INR value from original value and FX rate (Base to INR)
  const calculateINRValue = (
    originalValue: number | null,
    fxRateINR: number | null,
  ): number | null => {
    if (
      originalValue === null ||
      originalValue === undefined ||
      fxRateINR === null ||
      fxRateINR === undefined
    )
      return null;
    return originalValue * fxRateINR;
  };

  // Helper function to get relevant date based on invoice status
  const getRelevantDate = (
    invoice: Invoice,
  ): { date: string | null; label: string } => {
    if (invoice.status === "Planned") {
      return {
        date: invoice.finance_planned_date || invoice.operational_planned_date,
        label: invoice.finance_planned_date
          ? "Finance Planned"
          : "Operational Planned",
      };
    } else if (invoice.status === "Invoiced" || invoice.status === "Delayed") {
      return {
        date: invoice.invoice_date,
        label: "Invoice Date",
      };
    } else if (invoice.status === "Received") {
      return {
        date: invoice.payment_received_date,
        label: "Payment Date",
      };
    } else {
      return {
        date:
          invoice.invoice_date ||
          invoice.finance_planned_date ||
          invoice.operational_planned_date,
        label: "Date",
      };
    }
  };

  // Helper function to get relevant value based on invoice status
  const getRelevantValue = (
    invoice: Invoice,
    fxRateINR: number | null,
  ): {
    value: number | null;
    currency: string | null;
    inrValue: number | null;
  } => {
    let value: number | null = null;
    let currency: string | null = invoice.currency;

    if (invoice.status === "Planned") {
      value = invoice.est_value_original;
    } else if (invoice.status === "Invoiced" || invoice.status === "Delayed") {
      value = invoice.invoiced_value_original;
    } else if (invoice.status === "Received") {
      value = invoice.payment_received_value;
    } else {
      value = invoice.invoiced_value_original || invoice.est_value_original;
    }

    const inrValue = calculateINRValue(value, fxRateINR);

    return { value, currency, inrValue };
  };

  // Toggle expanded state for invoice USD details
  const toggleInvoiceExpanded = (invoiceId: number) => {
    setExpandedInvoiceIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(invoiceId)) {
        newSet.delete(invoiceId);
      } else {
        newSet.add(invoiceId);
      }
      return newSet;
    });
  };

  return (
    <Box sx={{ maxWidth: "100%", mx: "auto", position: "relative" }}>
      {/* <Box sx={{ mb: { xs: 2, sm: 3, md: 4 } }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 700,
            fontSize: {
              xs: "1rem",
              sm: "1.15rem",
              md: "clamp(1.15rem, 0.8vw, 1.3rem)",
            },
            lineHeight: 1.2,
          }}
        >
          Invoices
        </Typography>
      </Box> */}

      {invoiceError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setInvoiceError(null)}
        >
          {invoiceError}
        </Alert>
      )}

      <Box>
          {/* Filters */}
          <Paper
  sx={{
    p: { xs: 0.5, sm: 0.5, md: 1.5 },
    mb: { xs: 0.5, sm: 0.5, md: 1.5 },
  }}
>
  <Typography
    variant="h6"
    gutterBottom
    sx={{
      fontWeight: 600,
      mb: { xs: 0.5, sm: 0.5, md: 1 },
      pb: 1,
      borderBottom: 2,
      borderColor: "primary.main",
      fontSize: {
        xs: "0.95rem",
        sm: "1.05rem",
        md: "1.05rem",
        lg: "1.05rem",
      },
    }}
  >
    Filter Invoices
  </Typography>

  <Grid container spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
    {/* Client */}
    <Grid item xs={12} sm={6} md={2}>
      <Autocomplete
        options={invoiceFilterClientNames}
        value={invoiceFilters.client_name || null}
        onChange={(_e, newValue) =>
          setInvoiceFilters({
            ...invoiceFilters,
            client_name: newValue || "",
          })
        }
        loading={loadingInvoiceFilterClients}
        noOptionsText={
          loadingInvoiceFilterClients
            ? "Loading clients..."
            : invoiceFilterClientNamesError
              ? "Failed to load clients"
              : "No clients available"
        }
        renderInput={(params) => (
          <TextField
            {...params}
            fullWidth
            size="small"
            label="Client"
            placeholder="Type to search..."
            sx={{
              "& .MuiInputBase-input": {
                fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.9rem" },
              },
              "& .MuiInputLabel-root": {
                fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.9rem" },
              },
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loadingInvoiceFilterClients ? (
                    <CircularProgress color="inherit" size={16} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Grid>

    {/* Project */}
    <Grid item xs={12} sm={6} md={2}>
      <Autocomplete
        options={invoiceFilterProjectNames}
        value={invoiceFilters.project_name || null}
        onChange={(_e, newValue) =>
          setInvoiceFilters({
            ...invoiceFilters,
            project_name: newValue || "",
          })
        }
        loading={loadingInvoiceFilterProjects}
        noOptionsText={
          loadingInvoiceFilterProjects
            ? "Loading projects..."
            : invoiceFilterProjectNamesError
              ? "Failed to load projects"
              : "No projects available"
        }
        renderInput={(params) => (
          <TextField
            {...params}
            fullWidth
            size="small"
            label="Project"
            placeholder="Type to search..."
            sx={{
              "& .MuiInputBase-input": {
                fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.9rem" },
              },
              "& .MuiInputLabel-root": {
                fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.9rem" },
              },
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loadingInvoiceFilterProjects ? (
                    <CircularProgress color="inherit" size={16} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Grid>

    {/* Status */}
    <Grid item xs={12} sm={6} md={2}>
      <FormControl
        fullWidth
        size="small"
        sx={{
          "& .MuiInputLabel-root": {
            fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.9rem" },
          },
          "& .MuiSelect-select": {
            fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.9rem" },
          },
        }}
      >
        <InputLabel>Status</InputLabel>
        <Select
          value={invoiceFilters.status || "All"}
          onChange={(e) =>
            setInvoiceFilters({
              ...invoiceFilters,
              status: e.target.value === "All" ? "" : e.target.value,
            })
          }
          label="Status"
        >
          <MenuItem value="All">All</MenuItem>
          <MenuItem value="Planned">Planned</MenuItem>
          <MenuItem value="Invoiced">Invoiced</MenuItem>
          <MenuItem value="Received">Received</MenuItem>
          <MenuItem value="Delayed">Delayed</MenuItem>
          <MenuItem value="Cancelled">Cancelled</MenuItem>
        </Select>
      </FormControl>
    </Grid>

    {/* From Date */}
    <Grid item xs={12} sm={6} md={2}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          label="From Date"
          value={
            invoiceFilters.from_date
              ? dayjs(invoiceFilters.from_date)
              : null
          }
          onChange={(newValue) =>
            setInvoiceFilters({
              ...invoiceFilters,
              from_date: newValue
                ? newValue.format("YYYY-MM-DD")
                : "",
            })
          }
          slotProps={{
            textField: {
              size: "small",
              fullWidth: true,
              InputLabelProps: { shrink: true },
              sx: {
                borderRadius: 2,
                "& .MuiInputBase-input": {
                  fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.9rem" },
                },
                "& .MuiInputLabel-root": {
                  fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.9rem" },
                },
              },
            },
          }}
          format="DD/MM/YYYY"
        />
      </LocalizationProvider>
    </Grid>

    {/* To Date */}
    <Grid item xs={12} sm={6} md={2}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          label="To Date"
          value={
            invoiceFilters.to_date
              ? dayjs(invoiceFilters.to_date)
              : null
          }
          onChange={(newValue) =>
            setInvoiceFilters({
              ...invoiceFilters,
              to_date: newValue ? newValue.format("YYYY-MM-DD") : "",
            })
          }
          slotProps={{
            textField: {
              size: "small",
              fullWidth: true,
              InputLabelProps: { shrink: true },
              sx: {
                borderRadius: 2,
                "& .MuiInputBase-input": {
                  fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.9rem" },
                },
                "& .MuiInputLabel-root": {
                  fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.9rem" },
                },
              },
            },
          }}
          format="DD/MM/YYYY"
        />
      </LocalizationProvider>
    </Grid>

    {/* Button */}
    <Grid item xs={12} sm={6} md={2}>
      <Button
        variant="contained"
        onClick={handleApplyInvoiceFilters}
        fullWidth
        sx={{
          textTransform: "none",
          fontWeight: 600,
          fontSize: {
            xs: "0.75rem",
            sm: "0.85rem",
            md: "0.9rem",
            lg: "0.95rem",
          },
          height: {
            xs: 36,
            sm: 38,
            md: 40,
            lg: 44,
          },
        }}
      >
        Apply Filters
      </Button>
    </Grid>
  </Grid>
</Paper>

          {/* Invoices List */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              bgcolor: "transparent",
              boxShadow: "none",
              border: "none",
            }}
          >
            {!loadingInvoices && invoiceTotal > 0 && (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, px: 0, py: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {(invoicePage - 1) * invoicePageSize + 1}-{Math.min(invoicePage * invoicePageSize, invoiceTotal)} of {invoiceTotal} invoices
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FileDownloadIcon />}
                    onClick={handleExportInvoicesCsv}
                    disabled={loadingInvoices || exportingInvoicesCsv}
                    sx={{ textTransform: "none", fontWeight: 600 }}
                  >
                    {exportingInvoicesCsv ? "Exporting..." : "Export CSV"}
                  </Button>
                  <TablePagination
                    component="div"
                    count={invoiceTotal}
                    page={invoicePage - 1}
                    onPageChange={(_, newPage) => setInvoicePage(newPage + 1)}
                    rowsPerPage={invoicePageSize}
                    onRowsPerPageChange={(e) => {
                      const next = parseInt(e.target.value, 10);
                      setInvoicePageSize(Number.isFinite(next) ? next : 20);
                      setInvoicePage(1);
                    }}
                    rowsPerPageOptions={[5, 10, 20, 50, 100]}
                    labelDisplayedRows={() => ""}
                    sx={{ border: 0 }}
                  />

                  {invoiceTotalPages > 1 && (
                    <Pagination
                      count={invoiceTotalPages}
                      page={invoicePage}
                      onChange={(_, pageNum) => setInvoicePage(pageNum)}
                      showFirstButton
                      showLastButton
                      color="primary"
                      size="small"
                      disabled={loadingInvoices}
                    />
                  )}
                </Box>
              </Box>
            )}
            {loadingInvoices ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
                <CircularProgress />
              </Box>
            ) : invoices.length === 0 ? (
              <Box sx={{ p: 6, textAlign: "center" }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Invoices Found
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  {invoiceFilters.status ||
                  invoiceFilters.client_name ||
                  invoiceFilters.project_name
                    ? "Try adjusting your filters to see more results."
                    : "No invoices are available yet for this view."}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ p: 0 }}>
                {invoices.map((invoice) => {
                  const fxRateUSD = invoice.fx_rate_usd ?? null;
                  const fxRateINR = invoice.fx_rate_inr ?? null;
                  const relevantDate = getRelevantDate(invoice);
                  const relevantValue = getRelevantValue(invoice, fxRateINR);
                  const isExpanded = expandedInvoiceIds.has(invoice.id);

                  // Calculate USD values for expanded view
                  const plannedUSD = calculateUSDValue(
                    invoice.est_value_original,
                    fxRateUSD,
                  );
                  const invoicedUSD = calculateUSDValue(
                    invoice.invoiced_value_original,
                    fxRateUSD,
                  );
                  const receivedUSD = calculateUSDValue(
                    invoice.payment_received_value,
                    fxRateUSD,
                  );
                  const currentUSD = calculateUSDValue(
                    relevantValue.value,
                    fxRateUSD,
                  );

                  // Determine status color for border
                  const statusColor =
                    invoice.status === "Received"
                      ? "success.main"
                      : invoice.status === "Cancelled"
                        ? "error.main"
                        : invoice.status === "Delayed"
                          ? "warning.main"
                          : invoice.status === "Invoiced"
                            ? "info.main"
                            : "primary.main";

                  return (
                    <Card
                      key={invoice.id}
                      elevation={0}
                      sx={{
                        mb: 2,
                        borderRadius: 2,
                        border: 1,
                        borderColor: "divider",
                        borderLeft: 4,
                        borderLeftColor: statusColor,
                        transition: "all 0.2s",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                          borderColor: "primary.200",
                        },
                      }}
                    >
                      <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                        <Grid container spacing={2} alignItems="center">
                          {/* Invoice Code & PO */}
                          <Grid item xs={12} sm={3} md={2.5}>
                            <Box>
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  fontWeight: 700,
                                  color: "text.primary",
                                  fontSize: {
                                    xs: "0.95rem",
                                    sm: "1rem",
                                    md: "1.05rem",
                                  },
                                  lineHeight: 1.2,
                                  mb: 0.5,
                                }}
                              >
                                {invoice.invoice_code}
                              </Typography>
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "primary.main",
                                    fontWeight: 600,
                                    bgcolor: "primary.50",
                                    fontSize: {
                                      xs: "0.65rem",
                                      sm: "0.7rem",
                                      md: "0.72rem",
                                    },
                                    px: { xs: 0.6, sm: 0.8 },
                                    py: { xs: 0.15, sm: 0.2 },
                                    borderRadius: 1,
                                  }}
                                >
                                  {`PO-${invoice.po_id}`}
                                </Typography>
                              </Stack>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                sx={{
                                  mt: 0.5,
                                  fontSize: {
                                    xs: "0.7rem",
                                    sm: "0.75rem",
                                    md: "0.78rem",
                                  },
                                }}
                                noWrap
                              >
                                {invoice.client_name || "-"}
                              </Typography>
                            </Box>
                          </Grid>

                          {/* Project & Description */}
                          <Grid item xs={12} sm={3} md={3}>
                            <Box>
                              <Typography
                                variant="body2"
                                fontWeight="600"
                                color="text.primary"
                                sx={{
                                  fontSize: {
                                    xs: "0.8rem",
                                    sm: "0.85rem",
                                    md: "0.9rem",
                                  },
                                }}
                                noWrap
                              >
                                {invoice.project_name || "-"}
                              </Typography>
                              <Tooltip title={invoice.description || "-"}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    fontSize: {
                                      xs: "0.7rem",
                                      sm: "0.75rem",
                                      md: "0.78rem",
                                    },
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                  }}
                                >
                                  {invoice.description || "-"}
                                </Typography>
                              </Tooltip>
                            </Box>
                          </Grid>

                          {/* Status & Date */}
                          <Grid item xs={6} sm={2} md={2}>
                            <Box>
                              <Chip
                                label={invoice.status || "-"}
                                size="small"
                                color={
                                  invoice.status === "Received"
                                    ? "success"
                                    : invoice.status === "Cancelled"
                                      ? "error"
                                      : invoice.status === "Delayed"
                                        ? "warning"
                                        : invoice.status === "Invoiced"
                                          ? "info"
                                          : "default"
                                }
                                sx={{
                                  fontWeight: 700,
                                  fontSize: {
                                    xs: "0.62rem",
                                    sm: "0.7rem",
                                    md: "0.72rem",
                                  },
                                  height: { xs: 22, sm: 24 },
                                  mb: 0.5,
                                }}
                              />
                              <Typography
                                variant="caption"
                                display="block"
                                color="text.secondary"
                                fontWeight="500"
                                sx={{
                                  fontSize: {
                                    xs: "0.68rem",
                                    sm: "0.75rem",
                                    md: "0.78rem",
                                  },
                                }}
                              >
                                {formatDate(relevantDate.date)}
                              </Typography>
                              <Typography
                                variant="caption"
                                display="block"
                                color="text.disabled"
                                sx={{
                                  fontSize: {
                                    xs: "0.6rem",
                                    sm: "0.65rem",
                                    md: "0.68rem",
                                  },
                                }}
                              >
                                {relevantDate.label}
                              </Typography>
                            </Box>
                          </Grid>

                          {/* Value */}
                          <Grid
                            item
                            xs={6}
                            sm={2}
                            md={2.5}
                            sx={{ textAlign: "right" }}
                          >
                            <Typography
                              variant="subtitle1"
                              fontWeight="700"
                              color="text.primary"
                              sx={{
                                fontSize: {
                                  xs: "0.92rem",
                                  sm: "1rem",
                                  md: "1.05rem",
                                },
                              }}
                            >
                              {formatCurrency(
                                relevantValue.value,
                                relevantValue.currency,
                              )}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              sx={{
                                fontSize: {
                                  xs: "0.65rem",
                                  sm: "0.7rem",
                                  md: "0.72rem",
                                },
                              }}
                            >
                              {relevantValue.inrValue
                                ? formatCurrency(
                                    relevantValue.inrValue,
                                    "INR",
                                  ) + " INR"
                                : "-"}
                            </Typography>
                          </Grid>

                          {/* Actions */}
                          <Grid
                            item
                            xs={12}
                            sm={2}
                            md={2}
                            sx={{
                              display: "flex",
                              justifyContent: "flex-end",
                              gap: 1,
                            }}
                          >
                            <Tooltip
                              title={
                                isExpanded ? "Hide Details" : "Show Details"
                              }
                            >
                              <IconButton
                                size="small"
                                onClick={() =>
                                  toggleInvoiceExpanded(invoice.id)
                                }
                                sx={{
                                  border: 1,
                                  borderColor: "divider",
                                  color: "text.secondary",
                                  "&:hover": {
                                    bgcolor: "action.hover",
                                    color: "primary.main",
                                  },
                                }}
                              >
                                {isExpanded ? (
                                  <ExpandLessIcon />
                                ) : (
                                  <ExpandMoreIcon />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit Invoice">
                              <IconButton
                                size="small"
                                disabled={can("update") === false}
                                onClick={() => handleOpenEditInvoice(invoice)}
                                sx={{
                                  border: 1,
                                  borderColor: "divider",
                                  color: "primary.main",
                                  "&:hover": {
                                    bgcolor: "primary.50",
                                    borderColor: "primary.200",
                                  },
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Invoice">
                              <IconButton
                                size="small"
                                color="error"
                                disabled={can("delete") === false}
                                onClick={() => handleDeleteInvoice(invoice)}
                                sx={{
                                  border: 1,
                                  borderColor: "divider",
                                  "&:hover": {
                                    bgcolor: "error.light",
                                    color: "white",
                                  },
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Grid>
                        </Grid>

                        {/* Warnings Section */}
                        {((invoice.days_delayed != null &&
                          invoice.days_delayed > 0) ||
                          (invoice.diff_finance_planned_date != null &&
                            invoice.diff_finance_planned_date > 0)) && (
                          <Stack
                            spacing={1}
                            sx={{
                              mt: 2,
                              p: 1.5,
                              bgcolor: "error.50",
                              borderRadius: 1.5,
                              border: 1,
                              borderColor: "error.100",
                            }}
                          >
                            {invoice.days_delayed != null &&
                              invoice.days_delayed > 0 && (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <AccessTimeIcon
                                    fontSize="small"
                                    color="error"
                                    sx={{ fontSize: { xs: 14, sm: 16 } }}
                                  />
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "error.main",
                                      fontSize: {
                                        xs: "0.75rem",
                                        sm: "0.8rem",
                                        md: "0.82rem",
                                      },
                                      fontWeight: 600,
                                    }}
                                  >
                                    Delayed by {invoice.days_delayed} days
                                  </Typography>
                                </Box>
                              )}

                            {invoice.diff_finance_planned_date != null &&
                              invoice.diff_finance_planned_date > 0 && (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <EventRepeatIcon
                                    fontSize="small"
                                    color="error"
                                    sx={{ fontSize: { xs: 14, sm: 16 } }}
                                  />
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "error.main",
                                      fontSize: {
                                        xs: "0.75rem",
                                        sm: "0.8rem",
                                        md: "0.82rem",
                                      },
                                      fontWeight: 600,
                                    }}
                                  >
                                    Operational planned date changed by{" "}
                                    {invoice.diff_finance_planned_date} days
                                    from financial date.
                                  </Typography>
                                </Box>
                              )}
                          </Stack>
                        )}

                        {/* Expanded USD Details */}
                        {isExpanded && (
                          <Box
                            sx={{
                              mt: 2.5,
                              pt: 2,
                              borderTop: 1,
                              borderColor: "divider",
                              bgcolor: "background.paper",
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{
                                mb: 2,
                                fontSize: {
                                  xs: "0.7rem",
                                  sm: "0.75rem",
                                  md: "0.78rem",
                                },
                                fontWeight: 700,
                                color: "text.secondary",
                                textTransform: "uppercase",
                                letterSpacing: 0.5,
                              }}
                            >
                              Currency Details (USD Conversion)
                            </Typography>
                            <Grid container spacing={3}>
                              {invoice.est_value_original && plannedUSD && (
                                <Grid item xs={6} sm={3}>
                                  <Paper
                                    elevation={0}
                                    sx={{
                                      p: 1.5,
                                      bgcolor: "grey.50",
                                      borderRadius: 1.5,
                                      border: 1,
                                      borderColor: "divider",
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{
                                        fontSize: {
                                          xs: "0.65rem",
                                          sm: "0.7rem",
                                          md: "0.72rem",
                                        },
                                      }}
                                    >
                                      Planned Value (USD)
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      fontWeight="700"
                                      color="text.primary"
                                      sx={{
                                        mt: 0.5,
                                        fontSize: {
                                          xs: "0.8rem",
                                          sm: "0.85rem",
                                          md: "0.9rem",
                                        },
                                      }}
                                    >
                                      {formatCurrency(plannedUSD, "USD")}
                                    </Typography>
                                  </Paper>
                                </Grid>
                              )}
                              {invoice.invoiced_value_original &&
                                invoicedUSD && (
                                  <Grid item xs={6} sm={3}>
                                    <Paper
                                      elevation={0}
                                      sx={{
                                        p: 1.5,
                                        bgcolor: "info.50",
                                        borderRadius: 1.5,
                                        border: 1,
                                        borderColor: "info.100",
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        color="info.main"
                                        sx={{
                                          fontSize: {
                                            xs: "0.65rem",
                                            sm: "0.7rem",
                                            md: "0.72rem",
                                          },
                                          fontWeight: 600,
                                        }}
                                      >
                                        Invoiced Value (USD)
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        fontWeight="700"
                                        color="info.dark"
                                        sx={{
                                          mt: 0.5,
                                          fontSize: {
                                            xs: "0.8rem",
                                            sm: "0.85rem",
                                            md: "0.9rem",
                                          },
                                        }}
                                      >
                                        {formatCurrency(invoicedUSD, "USD")}
                                      </Typography>
                                    </Paper>
                                  </Grid>
                                )}
                              {invoice.payment_received_value &&
                                receivedUSD && (
                                  <Grid item xs={6} sm={3}>
                                    <Paper
                                      elevation={0}
                                      sx={{
                                        p: 1.5,
                                        bgcolor: "success.50",
                                        borderRadius: 1.5,
                                        border: 1,
                                        borderColor: "success.100",
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        color="success.main"
                                        sx={{
                                          fontSize: {
                                            xs: "0.65rem",
                                            sm: "0.7rem",
                                            md: "0.72rem",
                                          },
                                          fontWeight: 600,
                                        }}
                                      >
                                        Payment Received (USD)
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        fontWeight="700"
                                        color="success.dark"
                                        sx={{
                                          mt: 0.5,
                                          fontSize: {
                                            xs: "0.8rem",
                                            sm: "0.85rem",
                                            md: "0.9rem",
                                          },
                                        }}
                                      >
                                        {formatCurrency(receivedUSD, "USD")}
                                      </Typography>
                                    </Paper>
                                  </Grid>
                                )}
                              {currentUSD && (
                                <Grid item xs={6} sm={3}>
                                  <Paper
                                    elevation={0}
                                    sx={{
                                      p: 1.5,
                                      bgcolor: "primary.50",
                                      borderRadius: 1.5,
                                      border: 1,
                                      borderColor: "primary.100",
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      color="primary.main"
                                      sx={{
                                          fontSize: {
                                            xs: "0.65rem",
                                            sm: "0.7rem",
                                            md: "0.72rem",
                                          },
                                        fontWeight: 600,
                                      }}
                                    >
                                      Current Value (USD)
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      fontWeight="700"
                                      color="primary.dark"
                                        sx={{
                                          mt: 0.5,
                                          fontSize: {
                                            xs: "0.8rem",
                                            sm: "0.85rem",
                                            md: "0.9rem",
                                          },
                                        }}
                                    >
                                      {formatCurrency(currentUSD, "USD")}
                                    </Typography>
                                  </Paper>
                                </Grid>
                              )}
                            </Grid>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            )}
          </Paper>
        </Box>

      {/* Edit Invoice Dialog */}
      <Dialog
        open={openEditInvoice}
        onClose={handleCloseEditInvoice}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: 2, sm: 2.5 },
            mx: { xs: 1.5, sm: "auto" },
          },
        }}
      >
        <DialogTitle
          sx={{
            px: { xs: 2, sm: 3 },
            py: { xs: 1.5, sm: 2 },
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography
              variant="h5"
              component="span"
              sx={{
                fontWeight: 800,
                color: "primary.main",
                fontSize: "clamp(1.05rem, 1.6vw, 1.5rem)",
                lineHeight: 1.2,
              }}
            >
              {editingInvoice?.invoice_code}
            </Typography>
            {editingInvoice && (
              <Chip
                label={invoiceEditData.status || editingInvoice.status || "-"}
                size="small"
                color={
                  (invoiceEditData.status || editingInvoice.status) ===
                  "Received"
                    ? "success"
                    : (invoiceEditData.status || editingInvoice.status) ===
                        "Cancelled"
                      ? "error"
                      : (invoiceEditData.status || editingInvoice.status) ===
                          "Delayed"
                        ? "warning"
                        : "default"
                }
                sx={{
                  ml: 2,
                  fontWeight: 600,
                  fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.8rem" },
                  height: { xs: 22, sm: 24, md: 26 },
                }}
              />
            )}
          </Box>
          <IconButton
            onClick={handleCloseEditInvoice}
            size="small"
            sx={{
              width: { xs: 34, sm: 38, md: 40 },
              height: { xs: 34, sm: 38, md: 40 },
              border: 1,
              borderColor: "divider",
              "&:hover": {
                bgcolor: "action.hover",
              },
            }}
          >
            <CloseIcon sx={{ fontSize: { xs: 18, sm: 20, md: 22 } }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 3 } }}>
          {invoiceEditError && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                borderRadius: 2,
                fontSize: { xs: "0.85rem", sm: "0.9rem", md: "0.95rem" },
              }}
              onClose={() => setInvoiceEditError(null)}
            >
              {invoiceEditError}
            </Alert>
          )}

          {editingInvoice &&
            (() => {
              // Get FX rates: edited value > invoice value
              const currentFxRateUSD =
                invoiceEditData.fx_rate_usd !== undefined &&
                invoiceEditData.fx_rate_usd !== null
                  ? invoiceEditData.fx_rate_usd
                  : (editingInvoice.fx_rate_usd ?? null);
              const currentFxRateINR =
                invoiceEditData.fx_rate_inr !== undefined &&
                invoiceEditData.fx_rate_inr !== null
                  ? invoiceEditData.fx_rate_inr
                  : (editingInvoice.fx_rate_inr ?? null);
              const currentPlannedValue =
                invoiceEditData.est_value_original !== undefined
                  ? invoiceEditData.est_value_original
                  : editingInvoice.est_value_original;
              const currentInvoicedValue =
                invoiceEditData.invoiced_value_original !== undefined
                  ? invoiceEditData.invoiced_value_original
                  : editingInvoice.invoiced_value_original;

              return (
                <>
                  {/* Summary Cards */}
                  <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 3 }}>
                    {currentPlannedValue && (
                      <Grid item xs={12} sm={6} md={3}>
                        <Card
                          elevation={0}
                          sx={{
                            bgcolor: "primary.50",
                            border: 1,
                            borderColor: "primary.200",
                            borderRadius: { xs: 1.5, sm: 2 },
                          }}
                        >
                          <CardContent
                            sx={{
                              p: { xs: 1.5, sm: 2 },
                              "&:last-child": { pb: { xs: 1.5, sm: 2 } },
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                textTransform: "uppercase",
                                letterSpacing: 0.5,
                                fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                              }}
                            >
                              Planned Value
                            </Typography>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 700,
                                color: "primary.main",
                                mt: 0.5,
                                fontSize: "clamp(1.05rem, 1.4vw, 1.35rem)",
                                lineHeight: 1.2,
                              }}
                            >
                              {formatCurrency(
                                currentPlannedValue,
                                editingInvoice.currency,
                              )}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                              }}
                            >
                              {editingInvoice.currency || "-"}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    )}
                    {currentPlannedValue && currentFxRateUSD && (
                      <Grid item xs={12} sm={6} md={3}>
                        <Card
                          elevation={0}
                          sx={{
                            bgcolor: "success.50",
                            border: 1,
                            borderColor: "success.200",
                            borderRadius: { xs: 1.5, sm: 2 },
                          }}
                        >
                          <CardContent
                            sx={{
                              p: { xs: 1.5, sm: 2 },
                              "&:last-child": { pb: { xs: 1.5, sm: 2 } },
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                textTransform: "uppercase",
                                letterSpacing: 0.5,
                                fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                              }}
                            >
                              Planned (USD)
                            </Typography>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 700,
                                color: "success.main",
                                mt: 0.5,
                                fontSize: "clamp(1.05rem, 1.4vw, 1.35rem)",
                                lineHeight: 1.2,
                              }}
                            >
                              {formatCurrency(
                                calculateUSDValue(
                                  currentPlannedValue,
                                  currentFxRateUSD,
                                ),
                                "USD",
                              )}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                              }}
                            >
                              USD
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    )}
                    {currentPlannedValue && currentFxRateINR && (
                      <Grid item xs={12} sm={6} md={3}>
                        <Card
                          elevation={0}
                          sx={{
                            bgcolor: "info.50",
                            border: 1,
                            borderColor: "info.200",
                            borderRadius: { xs: 1.5, sm: 2 },
                          }}
                        >
                          <CardContent
                            sx={{
                              p: { xs: 1.5, sm: 2 },
                              "&:last-child": { pb: { xs: 1.5, sm: 2 } },
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                textTransform: "uppercase",
                                letterSpacing: 0.5,
                                fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                              }}
                            >
                              Planned (INR)
                            </Typography>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 700,
                                color: "info.main",
                                mt: 0.5,
                                fontSize: "clamp(1.05rem, 1.4vw, 1.35rem)",
                                lineHeight: 1.2,
                              }}
                            >
                              {formatCurrency(
                                calculateINRValue(
                                  currentPlannedValue,
                                  currentFxRateINR,
                                ),
                                "INR",
                              )}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                              }}
                            >
                              INR
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    )}
                    {currentInvoicedValue && (
                      <Grid item xs={12} sm={6} md={3}>
                        <Card
                          elevation={0}
                          sx={{
                            bgcolor: "warning.50",
                            border: 1,
                            borderColor: "warning.200",
                            borderRadius: { xs: 1.5, sm: 2 },
                          }}
                        >
                          <CardContent
                            sx={{
                              p: { xs: 1.5, sm: 2 },
                              "&:last-child": { pb: { xs: 1.5, sm: 2 } },
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                textTransform: "uppercase",
                                letterSpacing: 0.5,
                                fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                              }}
                            >
                              Invoiced Value
                            </Typography>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 700,
                                color: "warning.main",
                                mt: 0.5,
                                fontSize: "clamp(1.05rem, 1.4vw, 1.35rem)",
                                lineHeight: 1.2,
                              }}
                            >
                              {formatCurrency(
                                currentInvoicedValue,
                                editingInvoice.currency,
                              )}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                              }}
                            >
                              {editingInvoice.currency || "-"}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    )}
                  </Grid>

                  {/* Invoice Information Card */}
                  <Card
                    elevation={1}
                    sx={{
                      mb: 3,
                      borderRadius: 2,
                      border: 1,
                      borderColor: "divider",
                    }}
                  >
                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                        {/* Invoice Information */}

                      <Typography
                        variant="h6"
                        sx={{
                          mb: 3,
                          fontWeight: 600,
                          color: "text.primary",
                          pb: 1,
                          borderBottom: 2,
                          borderColor: "primary.main",
                          fontSize: { xs: "1rem", sm: "1.1rem", md: "1.2rem" },
                        }}
                      >
                        Invoice Information
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={4}>
                          <Box sx={{ mb: 2 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                                textTransform: "uppercase",
                                letterSpacing: 0.5,
                              }}
                            >
                              Client
                            </Typography>
                            <Typography
                              variant="body1"
                              fontWeight="600"
                              sx={{
                                mt: 0.5,
                                fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" },
                              }}
                            >
                              {editingInvoice.client_name || "-"}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <Box sx={{ mb: 2 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                                textTransform: "uppercase",
                                letterSpacing: 0.5,
                              }}
                            >
                              Project
                            </Typography>
                            <Typography
                              variant="body1"
                              fontWeight="600"
                              sx={{
                                mt: 0.5,
                                fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" },
                              }}
                            >
                              {editingInvoice.project_name || "-"}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <Box sx={{ mb: 2 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                                textTransform: "uppercase",
                                letterSpacing: 0.5,
                              }}
                            >
                              PO Code
                            </Typography>
                            <Typography
                              variant="body1"
                              fontWeight="600"
                              sx={{
                                mt: 0.5,
                                color: "primary.main",
                                fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" },
                              }}
                            >
                              {`PO-${editingInvoice.po_id}`}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </>
              );
            })()}

          {/* Edit Form */}
          <Card
            elevation={1}
            sx={{ borderRadius: 2, border: 1, borderColor: "divider" }}
          >
                {/* Edit Invoice Details */}

            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  fontWeight: 600,
                  color: "text.primary",
                  pb: 1,
                  borderBottom: 2,
                  borderColor: "primary.main",
                  fontSize: { xs: "1rem", sm: "1.1rem", md: "1.2rem" },
                }}
              >
                Edit Invoice Details
              </Typography>

              <Grid container spacing={3}>
                {/* Status Selection */}
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 2,
                      fontWeight: 600,
                      color: "text.secondary",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                    }}
                  >
                    Status
                  </Typography>
                </Grid>


            



                {/* status */}

                <Grid item xs={12} sm={6} md={4}>
                  <FormControl
                    fullWidth
                    sx={{
                      "& .MuiInputLabel-root": {
                        fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                      },
                      "& .MuiSelect-select": {
                        fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                      },
                    }}
                  >
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={invoiceEditData.status || ""}
                      onChange={(e) =>
                        setInvoiceEditData({
                          ...invoiceEditData,
                          status: e.target.value,
                        })
                      }
                      label="Status"
                    >
                      <MenuItem value="Planned">Planned</MenuItem>
                      <MenuItem value="Invoiced">Invoiced</MenuItem>
                      <MenuItem value="Received">Received</MenuItem>
                      {/* <MenuItem value="Delayed">Delayed</MenuItem> */}
                      <MenuItem value="Cancelled">Cancelled</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* payment terms */}

                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Payment Terms"
                    value={invoiceEditData.payment_terms || ""}
                    onChange={(e) =>
                      setInvoiceEditData({
                        ...invoiceEditData,
                        payment_terms: e.target.value || null,
                      })
                    }
                    fullWidth
                    sx={{
                      "& .MuiInputLabel-root": {
                        fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                      },
                      "& .MuiInputBase-input": {
                        fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                      },
                      "& .MuiFormHelperText-root": {
                        fontSize: { xs: "0.65rem", sm: "0.7rem" },
                      },
                    }}
                  />
                </Grid>


                {/* Planned Section */}
                <Grid item xs={12} sx={{ mt: 1 }}>
                  <Divider sx={{ my: 2 }} />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 2,
                      fontWeight: 600,
                      color: "text.secondary",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                    }}
                  >
                    Planned Details
                  </Typography>
                </Grid>

                {/* planned value */}

                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Planned value"
                    name="est_value_original"
                    type="number"
                    required
                    value={
                      invoiceEditData.est_value_original !== undefined &&
                      invoiceEditData.est_value_original !== null
                        ? invoiceEditData.est_value_original
                        : ""
                    }
                    onChange={(e) => {
                      setInvoicePlannedValueFieldError(null);
                      setInvoiceEditData({
                        ...invoiceEditData,
                        est_value_original: e.target.value
                          ? parseFloat(e.target.value)
                          : null,
                      });
                    }}
                    fullWidth
                    inputProps={{ step: "any", min: "0" }}
                    error={Boolean(invoicePlannedValueFieldError)}
                    helperText={invoicePlannedValueFieldError || undefined}
                    FormHelperTextProps={{
                      sx: {
                        color: "error.main",
                        fontSize: { xs: "0.65rem", sm: "0.7rem" },
                        m: 0,
                        mt: 0.5,
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontWeight: 700, letterSpacing: 0.02 }}
                          >
                            {editingInvoice?.currency
                              ? String(editingInvoice.currency).toUpperCase()
                              : "—"}
                          </Typography>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiInputLabel-root": {
                        fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                      },
                      "& .MuiInputBase-input": {
                        fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                      },
                      "& .MuiFormHelperText-root": {
                        fontSize: { xs: "0.65rem", sm: "0.7rem" },
                      },
                    }}
                  />
                </Grid>

                {/* Finance Planned Date */}
                <Grid item xs={12} sm={6} md={4}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Finance Planned Date"
                      value={
                        invoiceEditData.finance_planned_date
                          ? dayjs(invoiceEditData.finance_planned_date)
                          : null
                      }
                      onChange={(newValue) =>
                        setInvoiceEditData({
                          ...invoiceEditData,
                          finance_planned_date: newValue
                            ? newValue.format("YYYY-MM-DD")
                            : null,
                        })
                      }
                      readOnly
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          InputLabelProps: { shrink: true },
                          InputProps: { readOnly: true },
                          sx: {
                            "& .MuiInputLabel-root": {
                              fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                            },
                            "& .MuiInputBase-input": {
                              fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                            },
                            "& .MuiFormHelperText-root": {
                              fontSize: { xs: "0.65rem", sm: "0.7rem" },
                            },
                          },
                        },
                      }}
                      format="DD/MM/YYYY"
                    />
                  </LocalizationProvider>
                </Grid>
                {/* Operational Planned Date */}
                <Grid item xs={12} sm={6} md={4}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Operational Planned Date"
                      value={
                        invoiceEditData.operational_planned_date
                          ? dayjs(invoiceEditData.operational_planned_date)
                          : null
                      }
                      onChange={(newValue) =>
                        setInvoiceEditData({
                          ...invoiceEditData,
                          operational_planned_date: newValue
                            ? newValue.format("YYYY-MM-DD")
                            : null,
                        })
                      }
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          InputLabelProps: { shrink: true },
                          sx: {
                            "& .MuiInputLabel-root": {
                              fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                            },
                            "& .MuiInputBase-input": {
                              fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                            },
                            "& .MuiFormHelperText-root": {
                              fontSize: { xs: "0.65rem", sm: "0.7rem" },
                            },
                          },
                        },
                      }}
                      format="DD/MM/YYYY"
                    />
                  </LocalizationProvider>
                </Grid>
                {/* Service Month */}
                <Grid item xs={12} sm={6} md={4}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Service Month"
                      views={["year", "month"]}
                      value={
                        invoiceEditData.service_month_date
                          ? dayjs(invoiceEditData.service_month_date)
                          : null
                      }
                      onChange={(newValue) =>
                        setInvoiceEditData({
                          ...invoiceEditData,
                          service_month_date: newValue
                            ? newValue.format("YYYY-MM-01")
                            : null,
                        })
                      }
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          InputLabelProps: { shrink: true },
                          sx: {
                            "& .MuiInputLabel-root": {
                              fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                            },
                            "& .MuiInputBase-input": {
                              fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                            },
                            "& .MuiFormHelperText-root": {
                              fontSize: { xs: "0.65rem", sm: "0.7rem" },
                            },
                          },
                        },
                      }}
                      format="MMM YYYY"
                    />
                  </LocalizationProvider>
                </Grid>

                {/* Invoiced Section */}
                <Grid item xs={12} sx={{ mt: 1 }}>
                  <Divider sx={{ my: 2 }} />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 2,
                      fontWeight: 600,
                      color: "text.secondary",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                    }}
                  >
                    Invoiced Details
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Invoice Date"
                      value={
                        invoiceEditData.invoice_date
                          ? dayjs(invoiceEditData.invoice_date)
                          : null
                      }
                      onChange={(newValue) =>
                        setInvoiceEditData({
                          ...invoiceEditData,
                          invoice_date: newValue
                            ? newValue.format("YYYY-MM-DD")
                            : null,
                        })
                      }
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          InputLabelProps: { shrink: true },
                          sx: {
                            "& .MuiInputLabel-root": {
                              fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                            },
                            "& .MuiInputBase-input": {
                              fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                            },
                            "& .MuiFormHelperText-root": {
                              fontSize: { xs: "0.65rem", sm: "0.7rem" },
                            },
                          },
                        },
                      }}
                      format="DD/MM/YYYY"
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Invoiced Value (Original)"
                    type="number"
                    value={invoiceEditData.invoiced_value_original || ""}
                    onChange={(e) =>
                      setInvoiceEditData({
                        ...invoiceEditData,
                        invoiced_value_original: e.target.value
                          ? parseFloat(e.target.value)
                          : null,
                      })
                    }
                    fullWidth
                    inputProps={{ step: "0.01", min: "0" }}
                    sx={{
                      "& .MuiInputLabel-root": {
                        fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                      },
                      "& .MuiInputBase-input": {
                        fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                      },
                      "& .MuiFormHelperText-root": {
                        fontSize: { xs: "0.65rem", sm: "0.7rem" },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="FX Rate (Base to USD)"
                    type="number"
                    value={(() => {
                      if (
                        invoiceEditData.fx_rate_usd !== undefined &&
                        invoiceEditData.fx_rate_usd !== null
                      ) {
                        return invoiceEditData.fx_rate_usd;
                      }
                      if (
                        editingInvoice?.fx_rate_usd !== undefined &&
                        editingInvoice.fx_rate_usd !== null
                      ) {
                        return editingInvoice.fx_rate_usd;
                      }
                      return "";
                    })()}
                    onChange={(e) => {
                      const newFxRate = e.target.value
                        ? parseFloat(e.target.value)
                        : null;
                      setInvoiceEditData({
                        ...invoiceEditData,
                        fx_rate_usd: newFxRate,
                      });
                    }}
                    fullWidth
                    inputProps={{ step: "0.000001", min: "0" }}
                    helperText="FX rate to convert base currency to USD"
                    sx={{
                      "& .MuiInputLabel-root": {
                        fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                      },
                      "& .MuiInputBase-input": {
                        fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                      },
                      "& .MuiFormHelperText-root": {
                        fontSize: { xs: "0.65rem", sm: "0.7rem" },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="FX Rate (Base to INR)"
                    type="number"
                    value={(() => {
                      if (
                        invoiceEditData.fx_rate_inr !== undefined &&
                        invoiceEditData.fx_rate_inr !== null
                      ) {
                        return invoiceEditData.fx_rate_inr;
                      }
                      if (
                        editingInvoice?.fx_rate_inr !== undefined &&
                        editingInvoice.fx_rate_inr !== null
                      ) {
                        return editingInvoice.fx_rate_inr;
                      }
                      return "";
                    })()}
                    onChange={(e) => {
                      const newFxRate = e.target.value
                        ? parseFloat(e.target.value)
                        : null;
                      setInvoiceEditData({
                        ...invoiceEditData,
                        fx_rate_inr: newFxRate,
                      });
                    }}
                    fullWidth
                    inputProps={{ step: "0.000001", min: "0" }}
                    helperText="FX rate to convert base currency to INR"
                    sx={{
                      "& .MuiInputLabel-root": {
                        fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                      },
                      "& .MuiInputBase-input": {
                        fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                      },
                      "& .MuiFormHelperText-root": {
                        fontSize: { xs: "0.65rem", sm: "0.7rem" },
                      },
                    }}
                  />
                </Grid>
                {/* Calculated Fields Logic Block */}
                {(() => {
                  const fxRateUSD =
                    invoiceEditData.fx_rate_usd !== undefined &&
                    invoiceEditData.fx_rate_usd !== null
                      ? invoiceEditData.fx_rate_usd
                      : (editingInvoice?.fx_rate_usd ?? null);
                  const fxRateINR =
                    invoiceEditData.fx_rate_inr !== undefined &&
                    invoiceEditData.fx_rate_inr !== null
                      ? invoiceEditData.fx_rate_inr
                      : (editingInvoice?.fx_rate_inr ?? null);
                  const plannedValue =
                    invoiceEditData.est_value_original !== undefined
                      ? invoiceEditData.est_value_original
                      : (editingInvoice?.est_value_original ?? null);
                  const invoicedValue =
                    invoiceEditData.invoiced_value_original !== undefined
                      ? invoiceEditData.invoiced_value_original
                      : (editingInvoice?.invoiced_value_original ?? null);

                  if (
                    (plannedValue && fxRateUSD) ||
                    (invoicedValue && fxRateUSD)
                  ) {
                    return (
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            mt: 1,
                            p: { xs: 1.5, sm: 2 },
                            bgcolor: "grey.50",
                            borderRadius: 1,
                            border: 1,
                            borderColor: "grey.300",
                          }}
                        >
                          {plannedValue && fxRateUSD && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              display="block"
                              sx={{
                                mb: 0.5,
                                fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                              }}
                            >
                              <strong>Planned Value:</strong> USD:{" "}
                              {formatCurrency(
                                calculateUSDValue(plannedValue, fxRateUSD),
                                "USD",
                              )}{" "}
                              | INR:{" "}
                              {formatCurrency(
                                calculateINRValue(
                                  plannedValue,
                                  fxRateINR || null,
                                ),
                                "INR",
                              )}
                            </Typography>
                          )}
                          {invoicedValue && fxRateUSD && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              display="block"
                              sx={{
                                fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                              }}
                            >
                              <strong>Invoiced Value:</strong> USD:{" "}
                              {formatCurrency(
                                calculateUSDValue(invoicedValue, fxRateUSD),
                                "USD",
                              )}{" "}
                              | INR:{" "}
                              {formatCurrency(
                                calculateINRValue(
                                  invoicedValue,
                                  fxRateINR || null,
                                ),
                                "INR",
                              )}
                            </Typography>
                          )}

                          {invoiceEditData.payment_received_value &&
                            fxRateUSD && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                display="block"
                                sx={{
                                  fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                                }}
                              >
                                <strong>Received Value:</strong> USD:{" "}
                                {formatCurrency(
                                  calculateUSDValue(
                                    invoiceEditData.payment_received_value,
                                    fxRateUSD,
                                  ),
                                  "USD",
                                )}{" "}
                                | INR:{" "}
                                {formatCurrency(
                                  calculateINRValue(
                                    invoiceEditData.payment_received_value,
                                    fxRateINR || null,
                                  ),
                                  "INR",
                                )}
                              </Typography>
                            )}
                        </Box>
                      </Grid>
                    );
                  }
                  return null;
                })()}

                {/* Received Section */}
                <Grid item xs={12} sx={{ mt: 1 }}>
                  <Divider sx={{ my: 2 }} />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 2,
                      fontWeight: 600,
                      color: "text.secondary",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                    }}
                  >
                    Received Details
                  </Typography>
                </Grid>
                {/* <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Payment Terms"
                    value={invoiceEditData.payment_terms || ""}
                    onChange={(e) =>
                      setInvoiceEditData({
                        ...invoiceEditData,
                        payment_terms: e.target.value || null,
                      })
                    }
                    fullWidth
                    sx={{
                      "& .MuiInputLabel-root": {
                        fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                      },
                      "& .MuiInputBase-input": {
                        fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                      },
                      "& .MuiFormHelperText-root": {
                        fontSize: { xs: "0.65rem", sm: "0.7rem" },
                      },
                    }}
                  />
                </Grid> */}
                <Grid item xs={12} sm={6} md={4}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Payment Received Date"
                      value={
                        invoiceEditData.payment_received_date
                          ? dayjs(invoiceEditData.payment_received_date)
                          : null
                      }
                      onChange={(newValue) =>
                        setInvoiceEditData({
                          ...invoiceEditData,
                          payment_received_date: newValue
                            ? newValue.format("YYYY-MM-DD")
                            : null,
                        })
                      }
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          InputLabelProps: { shrink: true },
                          sx: {
                            "& .MuiInputLabel-root": {
                              fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                            },
                            "& .MuiInputBase-input": {
                              fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                            },
                            "& .MuiFormHelperText-root": {
                              fontSize: { xs: "0.65rem", sm: "0.7rem" },
                            },
                          },
                        },
                      }}
                      format="DD/MM/YYYY"
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Payment Received Value"
                    type="number"
                    value={invoiceEditData.payment_received_value || ""}
                    onChange={(e) =>
                      setInvoiceEditData({
                        ...invoiceEditData,
                        payment_received_value: e.target.value
                          ? parseFloat(e.target.value)
                          : null,
                      })
                    }
                    fullWidth
                    inputProps={{ step: "0.01", min: "0" }}
                    sx={{
                      "& .MuiInputLabel-root": {
                        fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                      },
                      "& .MuiInputBase-input": {
                        fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                      },
                      "& .MuiFormHelperText-root": {
                        fontSize: { xs: "0.65rem", sm: "0.7rem" },
                      },
                    }}
                  />
                </Grid>

                {/* Remarks Section */}
                <Grid item xs={12} sx={{ mt: 1 }}>
                  <Divider sx={{ my: 2 }} />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 2,
                      fontWeight: 600,
                      color: "text.secondary",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                    }}
                  >
                    Remarks
                  </Typography>
                  <TextField
                    label="Remarks"
                    value={invoiceEditData.remarks || ""}
                    onChange={(e) =>
                      setInvoiceEditData({
                        ...invoiceEditData,
                        remarks: e.target.value || null,
                      })
                    }
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Add remarks or notes..."
                    sx={{
                      "& .MuiInputLabel-root": {
                        fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.85rem" },
                      },
                      "& .MuiInputBase-input": {
                        fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" },
                      },
                      "& .MuiFormHelperText-root": {
                        fontSize: { xs: "0.65rem", sm: "0.7rem" },
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </DialogContent>
        <DialogActions
          sx={{
            p: { xs: 2, sm: 2.5 },
            borderTop: 1,
            borderColor: "divider",
            gap: { xs: 1, sm: 1.25 },
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            justifyContent: { xs: "stretch", sm: "flex-end" },
          }}
        >
          <Button
            onClick={handleCloseEditInvoice}
            disabled={savingInvoice}
            sx={{
              textTransform: "none",
              fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.9rem" },
              minWidth: { xs: "100%", sm: 100, md: 110 },
              height: { xs: 34, sm: 36, md: 38 },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveInvoice}
            variant="contained"
            disabled={savingInvoice}
            startIcon={savingInvoice ? <CircularProgress size={16} /> : null}
            sx={{
              textTransform: "none",
              fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.9rem" },
              minWidth: { xs: "100%", sm: 140, md: 150 },
              height: { xs: 34, sm: 36, md: 38 },
              fontWeight: 600,
            }}
          >
            {savingInvoice ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>



      {/* Delete Invoice Confirmation Dialog */}
      <Dialog
        open={deleteInvoiceDialogOpen}
        onClose={handleCloseDeleteInvoiceDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontWeight: 600,
          }}
        >
          Confirm Delete
          <IconButton
            onClick={handleCloseDeleteInvoiceDialog}
            size="small"
            disabled={deletingInvoiceLoading}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            Are you sure you want to delete invoice{" "}
            <strong>{deletingInvoice?.invoice_code}</strong>?
            <br />
            <br />
            This action cannot be undone.
          </Alert>
          {deletingInvoice && (
            <Box sx={{ mt: 2, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Client:</strong> {deletingInvoice.client_name || "-"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Project:</strong> {deletingInvoice.project_name || "-"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Status:</strong> {deletingInvoice.status || "-"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>PO Code:</strong> {`PO-${deletingInvoice.po_id}`}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseDeleteInvoiceDialog}
            variant="outlined"
            disabled={deletingInvoiceLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeleteInvoice}
            variant="contained"
            color="error"
            disabled={deletingInvoiceLoading}
            startIcon={
              deletingInvoiceLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DeleteIcon />
              )
            }
          >
            {deletingInvoiceLoading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InvoicesPage;
